import { DEFAULT_LOCALE, getLanguageCode, isKnownLocaleTag } from "@pandas/core-ui";
import { createFileRoute, Outlet, redirect } from "@tanstack/react-router";
import i18next from "@/i18n";
import { visionSearchParamsSchema } from "@/routes/-search-params";
import { fetchPartnerDefaultLocale } from "@/utils/localeResolver";
import { resolvePartnerFromSearch } from "@/utils/resolvePartnerFromSearch";

export const Route = createFileRoute("/$locale")({
	validateSearch: (search) => visionSearchParamsSchema.parse(search),
	beforeLoad: async ({ params, search, location }) => {
		const parsedSearch = visionSearchParamsSchema.parse(search);

		if (isKnownLocaleTag(params.locale)) {
			// Canonical form is the 2-letter language code (el-GR → el, EN → en).
			const canonical = getLanguageCode(params.locale);
			if (canonical !== params.locale) {
				const pathParts = location.pathname.split("/");
				pathParts[1] = canonical;
				throw redirect({
					to: pathParts.join("/"),
					search: parsedSearch,
					replace: true,
				});
			}

			if (i18next.language !== canonical) {
				i18next.changeLanguage(canonical);
			}

			// If we're at just /$locale with no further path segments, redirect to /$locale/$partner
			const pathSegments = location.pathname.split("/").filter(Boolean);
			if (pathSegments.length === 1) {
				const resolvedPartner = await resolvePartnerFromSearch(parsedSearch);
				if (!resolvedPartner) {
					throw redirect({
						to: "/$locale/error",
						params: { locale: canonical },
						search: { code: "partner_not_found" },
						replace: true,
					});
				}
				throw redirect({
					to: "/$locale/$partner",
					params: { locale: canonical, partner: resolvedPartner },
					search: { ...parsedSearch, partnerCode: resolvedPartner },
					replace: true,
				});
			}

			return { locale: canonical };
		}

		// Not a valid locale — treat it as a partner code
		const partnerCode = params.locale;
		let resolved: string;
		try {
			resolved = await fetchPartnerDefaultLocale(partnerCode);
		} catch {
			throw redirect({
				href: `/${DEFAULT_LOCALE}/error?code=partner_not_found`,
				replace: true,
			});
		}
		const locale = getLanguageCode(resolved);
		const pathSegments = location.pathname.split("/").filter(Boolean);
		pathSegments[0] = partnerCode;
		const newPath = `/${locale}/${pathSegments.join("/")}`;
		throw redirect({
			to: newPath,
			search: { ...parsedSearch, partnerCode },
			replace: true,
		});
	},
	component: LocaleLayout,
});

function LocaleLayout() {
	return <Outlet />;
}
